import {
  createGlobalThemeContract,
  createGlobalVar,
  style,
  styleVariants,
} from "@vanilla-extract/css";
import {cardStateColors, dsColors, oneOffPalettes} from "../tokens";
import {applyThemeVars, createThemeBuilder, toNamedContract} from "./theme-utils";
import themeShape from "./theme-shape";

export const colorThemeVars = createGlobalThemeContract(
  toNamedContract(themeShape, "cdx-", {elementTheme: "el", componentTheme: "comp"})
);

// Registered as <color> so a theme's `light-dark()` resolves where it is declared and
// descendants inherit the finished colour instead of re-resolving it.
for (const name of Object.values(toNamedContract(themeShape.outer, "cdx-outer-"))) {
  createGlobalVar(name, {syntax: "<color>", inherits: true, initialValue: "transparent"});
}

export const elementThemeVars = createGlobalThemeContract(
  toNamedContract(themeShape.elementTheme.default, "cdx-el-")
);
export const elementThemes = styleVariants(colorThemeVars.elementTheme, (val) =>
  applyThemeVars(elementThemeVars, val)
);
export type ElementTheme = keyof typeof elementThemes;

export const hoverElementThemes = styleVariants(colorThemeVars.elementTheme, (val) => ({
  selectors: {
    "&:hover:not(:disabled)": applyThemeVars(elementThemeVars, val),
  },
}));

const buildTheme = createThemeBuilder(colorThemeVars);

export const baseTheme = buildTheme(null, themeShape, {
  name: "theme-base",
  asRoot: true,
});
export const lightTheme = buildTheme(
  baseTheme,
  {},
  {
    mode: "both",
    name: "theme-main",
  }
);

const darkTheme = buildTheme(baseTheme, {}, {mode: "dark", name: "theme-gray550"});

export const subThemeAware = style({});
export const brandSubThemeOnHover = style({});

const gray25adapt = buildTheme(
  baseTheme,
  {
    bg: [dsColors.gray25, dsColors.gray750],
    text: {
      secondary: [dsColors.gray600, dsColors.gray150],
    },
    border: {
      default: [dsColors.gray200, dsColors.gray400],
      light: [dsColors.gray150, dsColors.gray550],
    },
    table: {
      alternatingRowBg: [dsColors.gray50, dsColors.gray850],
    },
  },
  {
    name: "theme-sub-white",
  }
);

export const subThemes = {
  // `whiteTile` is used in e.g. the activity feed which appears on both dark and light bg (even within light mode)
  whiteTile: baseTheme,
  gray25Tile: gray25adapt,
  brand: buildTheme(
    baseTheme,
    {
      bg: [dsColors.purple25, dsColors.purple800],
      text: {
        primary: [dsColors.purple700, dsColors.purple25],
        secondary: [dsColors.purple600, dsColors.purple100],
        accent: [dsColors.purple500, dsColors.purple200],
      },
      border: {default: [dsColors.purple200, dsColors.purple400]},
      table: {
        alternatingRowBg: [dsColors.purple50, dsColors.purple750],
      },
    },
    {
      name: "theme-brand",
      secondarySelectors: [`.${brandSubThemeOnHover}:hover`],
    }
  ),
  warn: buildTheme(
    baseTheme,
    {
      bg: [cardStateColors.doc25, cardStateColors.doc600],
      text: {
        primary: [cardStateColors.doc700, cardStateColors.doc25],
        secondary: [cardStateColors.doc600, cardStateColors.doc50],
      },
      border: {
        default: [cardStateColors.doc200, cardStateColors.doc400],
        light: [cardStateColors.doc100, cardStateColors.doc500],
      },
      elementTheme: {
        default: themeShape.elementTheme.warn,
      },
    },
    {
      name: "theme-warn",
    }
  ),
  accent: buildTheme(
    baseTheme,
    {
      bg: [dsColors.active25, dsColors.active800],
      text: {
        primary: [dsColors.active700, dsColors.active25],
        secondary: [dsColors.active600, dsColors.active100],
      },
      border: {default: [dsColors.active200, dsColors.active600]},
    },
    {
      name: "theme-accent",
      secondarySelectors: [`.${subThemeAware} :is(pre,code,blockquote)`],
    }
  ),
  active: buildTheme(
    baseTheme,
    {
      bg: [dsColors.active25, dsColors.active600],
      text: {
        primary: [dsColors.active700, dsColors.white],
        secondary: [dsColors.active600, dsColors.active25],
      },
      border: {
        default: [dsColors.active200, dsColors.active400],
        light: [dsColors.active100, dsColors.active500],
      },
      elementTheme: {
        default: {
          main: {
            base: [dsColors.active400, dsColors.active500],
            lighter: [dsColors.active300, dsColors.active400],
            darker: [dsColors.active500, dsColors.active600],
          },
          subtle: {
            base: [dsColors.active50, dsColors.active100],
            lighter: [dsColors.active25, dsColors.active50],
            darker: [dsColors.active100, dsColors.active200],
          },
          contrast: {
            base: [dsColors.active25, dsColors.active50],
            lighter: [dsColors.white, dsColors.active25],
            darker: [dsColors.active50, dsColors.active100],
            onTransparent: [dsColors.active500, dsColors.active100],
            subtle: [dsColors.active600, dsColors.active700],
          },
          border: {
            base: [dsColors.active300, dsColors.active200],
          },
        },
      },
    },
    {
      name: "theme-active",
    }
  ),
  alert: buildTheme(
    baseTheme,
    {
      bg: [dsColors.alert25, dsColors.alert800],
      text: {
        primary: [dsColors.alert700, dsColors.alert25],
        secondary: [dsColors.alert600, dsColors.alert100],
      },
      border: {default: [dsColors.alert200, dsColors.alert600]},
      elementTheme: {
        default: themeShape.elementTheme.alert,
      },
    },
    {
      name: "theme-alert",
    }
  ),
};

const gray50 = buildTheme(
  lightTheme,
  {
    bg: [dsColors.gray50, dsColors.gray850],
    text: {
      secondary: [dsColors.gray600, dsColors.gray150],
    },
    border: {
      default: [dsColors.gray250, dsColors.gray500],
      light: [dsColors.gray100, dsColors.gray600],
    },
    table: {
      alternatingRowBg: [dsColors.gray100, dsColors.gray750],
    },
  },
  {name: "theme-gray50"}
);

const journeyBlue900 = buildTheme(
  darkTheme,
  {
    bg: oneOffPalettes.dampBlue900,
    border: {default: oneOffPalettes.dampBlue700, light: oneOffPalettes.dampBlue800},
    text: {primary: oneOffPalettes.dampBlue25, secondary: oneOffPalettes.dampBlue50},
    componentTheme: {
      input: {
        default: {
          bg: {
            base: oneOffPalettes.dampBlue950,
          },
          text: {
            primary: oneOffPalettes.dampBlue25,
            secondary: oneOffPalettes.dampBlue50,
          },
          border: {
            default: oneOffPalettes.dampBlue400,
          },
        },
      },
    },
  },
  {name: "theme-journeyBlue900"}
);

export const colorThemes = {
  white: lightTheme,
  forceWhite: buildTheme(lightTheme, {}, {mode: "forceLight", name: "theme-forceWhite"}),
  whiteAsContrast: buildTheme(
    lightTheme,
    {
      bg: [dsColors.white, dsColors.gray850],
      text: {
        secondary: [dsColors.gray550, dsColors.gray150],
      },
      border: {
        default: [dsColors.gray150, dsColors.gray500],
        light: [dsColors.gray100, dsColors.gray600],
      },
      table: {
        alternatingRowBg: [dsColors.gray25, dsColors.gray750],
      },
    },
    {name: "theme-white-contrast"}
  ),
  gray25: buildTheme(gray25adapt, {}, {name: "theme-gray25", mode: "both"}),
  gray50,
  forceGray50: buildTheme(gray50, {}, {mode: "forceLight", name: "theme-forceGray50"}),
  gray50Secondary: buildTheme(
    lightTheme,
    {
      bg: [dsColors.gray50, dsColors.gray650],
      text: {
        secondary: [dsColors.gray600, dsColors.gray100],
      },
      border: {
        default: [dsColors.gray250, dsColors.gray350],
        light: [dsColors.gray100, dsColors.gray500],
      },
      table: {
        alternatingRowBg: [dsColors.gray100, dsColors.gray550],
      },
    },
    {name: "theme-gray50Secondary"}
  ),

  gray550: darkTheme,
  gray700: buildTheme(
    darkTheme,
    {
      bg: dsColors.gray700,
      border: {default: dsColors.gray550},
    },
    {name: "theme-gray700"}
  ),
  gray750: buildTheme(
    darkTheme,
    {
      bg: dsColors.gray750,
      border: {default: dsColors.gray600},
    },
    {name: "theme-gray750"}
  ),
  gray850: buildTheme(gray50, {}, {name: "theme-gray850", mode: "dark"}),
  purple25: buildTheme(subThemes.brand, {}, {name: "theme-purple25", mode: "both"}),
  purple50: buildTheme(
    subThemes.brand,
    {
      bg: [dsColors.purple50, dsColors.purple700],
      text: {
        primary: [dsColors.purple750, dsColors.purple25],
        secondary: [dsColors.purple600, dsColors.purple100],
      },
      border: {default: [dsColors.purple200, dsColors.purple400]},
      table: {
        alternatingRowBg: [dsColors.purple100, dsColors.purple600],
      },
    },
    {name: "theme-purple50", mode: "both"}
  ),
  purple350: buildTheme(
    darkTheme,
    {
      bg: dsColors.purple350,
      text: {primary: dsColors.purple25, secondary: dsColors.purple100},
      border: {default: dsColors.purple250},
      table: {
        alternatingRowBg: dsColors.purple450,
      },
      elementTheme: {
        default: {
          main: {lighter: dsColors.purple450},
          border: {base: dsColors.purple250},
        },
      },
    },
    {name: "theme-purple350"}
  ),
  purple900: buildTheme(
    darkTheme,
    {
      bg: dsColors.purple900,
      text: {primary: dsColors.purple25, secondary: dsColors.purple150},
      border: {default: dsColors.purple600, light: dsColors.purple700},
    },
    {name: "theme-purple900"}
  ),
  // used for the hand area
  legacyDimPurple: buildTheme(
    darkTheme,
    {
      bg: "#464050",
      text: {primary: dsColors.purple50, secondary: dsColors.dimPurple150},
      border: {default: dsColors.dimPurple450, light: dsColors.dimPurple550},
    },
    {name: "theme-legacyDimPurple"}
  ),
  active25: buildTheme(subThemes.active, {}, {name: "theme-active25", mode: "both"}),
  active50: buildTheme(
    subThemes.active,
    {
      bg: [dsColors.active50, dsColors.active700],
    },
    {name: "theme-active25", mode: "both"}
  ),
  active600: buildTheme(subThemes.active, {}, {name: "theme-active600", mode: "dark"}),
  done800: buildTheme(
    // completed onboarding chapters
    darkTheme,
    {
      text: {primary: cardStateColors.done100, secondary: cardStateColors.done300},
    },
    {name: "theme-done800"}
  ),

  journeyDimBlue: buildTheme(
    darkTheme,
    {
      bg: "#25465E",
      border: {default: "#3e6887"},
      text: {primary: dsColors.white, secondary: dsColors.gray150},
    },
    {name: "theme-journeyDimBlue"}
  ),
  journeyBlue900,
  journeyBlue800: buildTheme(
    journeyBlue900,
    {
      bg: oneOffPalettes.dampBlue800,
      border: {default: oneOffPalettes.dampBlue600, light: oneOffPalettes.dampBlue700},
    },
    {name: "theme-journeyBlue800"}
  ),

  elementThemeBase: buildTheme(
    lightTheme,
    {
      bg: elementThemeVars.main.base,
      text: {
        primary: elementThemeVars.contrast.base,
        secondary: elementThemeVars.contrast.lighter,
      },
      border: {
        default: elementThemeVars.main.lighter,
      },
      // take the surrounding surface's outer colours despite the forced dark mode below
      outer: {shadow: "inherit", glow: "inherit", focusRing: "inherit"},
    },
    {
      name: "theme-elementThemeBase",
      // let's force a dark mode, such that a `+1` subtheme.active has a light text color
      // TBD: `neutral` should keep the context's mode though...
      mode: "dark",
    }
  ),
};
